// Ian - 2026-08-20 - Completes the category tile swatches on ian-collection-nav.
//
// Why this exists: Liquid caps collection.products at 50 per render and Alpine holds 106, so
// the server-rendered swatch row is built from whichever 50 the collection happens to return.
// Small categories lose out badly (Scarves rendered 1 of its 3 colourways).
//
// How it works: every product type in the collection fits inside the 50 cap on its OWN, so this
// asks Shopify's Section Rendering API to re-render the same section against each tile's filter
// URL. On that render collection.products is just that type's products, so the section's colour
// index is complete. We read the index out of the returned markup and repaint the dots.
//
// Costs one small request per filter-backed tile, on idle, cached in sessionStorage for the rest
// of the visit. Tiles pointing at a real collection are skipped: their server render is already
// complete. If anything fails the server-rendered dots simply stay as they are.
//
// Ian - 2026-09-16 - A tile's data-colnav-type can be a comma-separated list ("Glove, Hat, Scarf")
// since the section accepts one. The section also emits data-colnav-urls: one single-value filter
// URL per matched type, pipe-joined. Those are what get fetched, one request per type, because a
// section render against the combined URL only returns the LAST repeated value's products
// (measured on the draft: Glove&Hat&Scarf came back as Scarf alone). The palettes are merged in
// list order and painted once. Cache key is the raw list string, so a single-type tile's key and
// its single request are unchanged.
//
// Ian - 2026-09-16 - Filter tiles ("Length: Short") carry data-colnav-any. Their section render is
// already scoped to the filter, so every type in the returned index belongs to the tile and all of
// them are merged. data-colnav-type is prefixed "filter:" so the cache key cannot collide with a
// product type of the same name.
//
// Ian - 2026-09-16 - Combined filter tiles ("Type: Sweater, Length: Short") also carry an empty
// [data-colnav-count] line. The section payload's data-count is collection.products_count on the
// filtered render, i.e. the intersection count, which the parent render cannot know. It is written
// in and cached alongside the palette under the same key plus ":count".
(function () {
  'use strict';

  // Ian - 2026-08-20 - localStorage, not sessionStorage. A colourway range changes a few times
  // a year, so caching it for the session only meant every visitor paid the round trip on every
  // visit. With a TTL the dots are instant on the second view onwards. Bump CACHE_VERSION to
  // invalidate every stored palette at once.
  var CACHE = 'ian-colnav:';
  var CACHE_VERSION = 1;
  var CACHE_TTL = 12 * 60 * 60 * 1000;

  // Ian - the section emits its colour index as "Type~#hex|Type~#hex|". Split on the LAST tilde
  // so a product type containing one cannot corrupt the hex.
  function parseIndex(text) {
    var map = {};
    (text || '').split('|').forEach(function (entry) {
      if (!entry) return;
      var cut = entry.lastIndexOf('~');
      if (cut < 1) return;
      var type = entry.slice(0, cut);
      var hex = entry.slice(cut + 1);
      if (!type || !hex) return;
      if (!map[type]) map[type] = [];
      if (map[type].indexOf(hex) === -1) map[type].push(hex);
    });
    return map;
  }

  // Ian - 2026-08-31 - Builds one overflow label. Kept as a helper because there are now two,
  // one per breakpoint, and they must be byte-identical in structure to the server-rendered pair.
  function buildMore(count, variant) {
    var more = document.createElement('span');
    more.className = 'ian-colnav__swatch-more ian-colnav__swatch-more--' + variant;
    more.appendChild(document.createTextNode('+' + count));

    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = 'more colours';
    more.appendChild(sr);

    return more;
  }

  function paint(tile, hexes, limit, showMore, limitMobile) {
    var row = tile.querySelector('[data-colnav-swatches]');
    if (!row || !hexes.length) return;

    row.innerHTML = '';

    // Ian - 2026-08-31 - Mirrors the server render exactly: dots past the mobile limit are marked
    // --desktop and hidden by CSS under 990px, and both +N labels are emitted so CSS can pick the
    // one that matches. Doing the choosing in CSS rather than here means no resize listener, and
    // no wrong count in the moment between a resize and a repaint.
    hexes.slice(0, limit).forEach(function (hex, i) {
      var dot = document.createElement('span');
      dot.className = 'ian-colnav__swatch' + (i >= limitMobile ? ' ian-colnav__swatch--desktop' : '');
      dot.style.background = hex;
      row.appendChild(dot);
    });

    if (showMore) {
      if (hexes.length > limit) row.appendChild(buildMore(hexes.length - limit, 'desktop'));
      if (hexes.length > limitMobile) row.appendChild(buildMore(hexes.length - limitMobile, 'mobile'));
    }

    row.setAttribute('data-colnav-total', hexes.length);
  }

  // Ian - 2026-09-16 - "Glove, Hat, Scarf" -> ["Glove", "Hat", "Scarf"]; a single value stays one.
  function splitTypes(type) {
    return (type || '')
      .split(',')
      .map(function (t) { return t.trim(); })
      .filter(Boolean);
  }

  // Ian - 2026-09-16 - hexes for every listed type, merged in list order, no duplicates.
  function mergeHexes(map, types) {
    var out = [];
    types.forEach(function (t) {
      (map[t] || []).forEach(function (hex) {
        if (out.indexOf(hex) === -1) out.push(hex);
      });
    });
    return out;
  }

  // Ian - 2026-09-16 - writes "43 styles" / "1 style" into a reserved count line.
  function paintCount(tile, count) {
    var el = tile.querySelector('[data-colnav-count]');
    if (!el || typeof count !== 'number' || isNaN(count)) return;
    var noun = el.getAttribute('data-noun') || '';
    if (count === 1) noun = noun.replace(/s$/, '');
    el.textContent = count + ' ' + noun;
  }

  function readCache(key) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== CACHE_VERSION) return null;
      if (Date.now() - parsed.t > CACHE_TTL) return null;
      return parsed.h;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, value) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ v: CACHE_VERSION, t: Date.now(), h: value })
      );
    } catch (e) {
      // Ian - private browsing and full quotas both throw here. Not worth handling, the
      // fetch simply runs again next page view.
    }
  }

  function init(root) {
    if (!root || root.hasAttribute('data-colnav-ready')) return;
    root.setAttribute('data-colnav-ready', '');

    var sectionId = root.getAttribute('data-ian-colnav');
    var limit = parseInt(root.getAttribute('data-colnav-limit'), 10) || 8;
    var limitMobile = parseInt(root.getAttribute('data-colnav-limit-mobile'), 10) || 5;
    var showMore = root.getAttribute('data-colnav-more') === 'true';
    if (!sectionId) return;

    var tiles = root.querySelectorAll('[data-colnav-type][data-colnav-url]');
    var pending = [];

    // Ian - pass one, synchronous: anything already cached paints now, before idle. A repeat
    // visitor never sees the partial server-rendered row at all.
    Array.prototype.forEach.call(tiles, function (tile) {
      var type = tile.getAttribute('data-colnav-type');
      var url = tile.getAttribute('data-colnav-url');
      if (!type || !url) return;

      var cached = readCache(CACHE + sectionId + ':' + type);
      // Ian - 2026-09-16 - a tile with a reserved count line needs its count cached too
      var needsCount = !!tile.querySelector('[data-colnav-count]');
      var cachedCount = needsCount ? readCache(CACHE + sectionId + ':' + type + ':count') : null;
      if (cached && (!needsCount || cachedCount !== null)) {
        paint(tile, cached, limit, showMore, limitMobile);
        if (needsCount) paintCount(tile, cachedCount);
      } else {
        pending.push(tile);
      }
    });

    if (!pending.length) return;

    // Ian - pass two: only the palettes we do not hold go to the network, and only once the
    // browser is idle, so this never competes with LCP.
    schedule(function () {
      pending.forEach(fetchPalette);
    });

    // Ian - 2026-09-16 - one section render for one filter URL, resolving to its parsed index.
    function fetchIndex(url) {
      var joiner = url.indexOf('?') === -1 ? '?' : '&';
      var endpoint = url + joiner + 'section_id=' + encodeURIComponent(sectionId);

      return fetch(endpoint, { credentials: 'same-origin', priority: 'low' })
        .then(function (res) {
          return res.ok ? res.text() : Promise.reject(new Error(res.status));
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-ian-colnav-index]');
          if (!node) return { map: {}, count: NaN };
          return {
            map: parseIndex(node.textContent),
            count: parseInt(node.getAttribute('data-count'), 10)
          };
        });
    }

    function fetchPalette(tile) {
      var type = tile.getAttribute('data-colnav-type');
      var url = tile.getAttribute('data-colnav-url');
      var key = CACHE + sectionId + ':' + type;

      // Ian - 2026-09-16 - per-type URLs when the section emitted them, else the tile URL as before.
      var urls = (tile.getAttribute('data-colnav-urls') || '').split('|').filter(Boolean);
      if (!urls.length) urls = [url];

      Promise.all(urls.map(fetchIndex))
        .then(function (results) {
          var merged = {};
          results.forEach(function (result) {
            var map = result.map;
            Object.keys(map).forEach(function (t) {
              merged[t] = (merged[t] || []).concat(map[t]);
            });
          });

          // Ian - 2026-09-16 - a count is only meaningful from a single render of the tile's own URL
          if (urls.length === 1 && tile.querySelector('[data-colnav-count]')) {
            var count = results[0].count;
            if (!isNaN(count)) {
              writeCache(key + ':count', count);
              paintCount(tile, count);
            }
          }

          var types = tile.hasAttribute('data-colnav-any') ? Object.keys(merged) : splitTypes(type);
          var hexes = mergeHexes(merged, types);
          if (!hexes || !hexes.length) return;
          writeCache(key, hexes);
          paint(tile, hexes, limit, showMore, limitMobile);
        })
        .catch(function () {
          // Ian - leave the server-rendered dots in place. Partial beats empty.
        });
    }
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-ian-colnav]'),
      init
    );
  }

  function schedule(fn) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(fn, { timeout: 1000 });
    } else {
      window.setTimeout(fn, 200);
    }
  }

  // Ian - init runs as soon as the DOM is ready so cached palettes paint immediately. Only the
  // network pass inside it waits for idle.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Ian - theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector) return;
    init(event.target.querySelector('[data-ian-colnav]'));
  });
})();
